import { useCallback, useMemo } from 'react'
import { AttributeItem } from '../../ShedManagementTypes'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { actions } from '../../ShedManagementState'
import ContainerCheckbox from '../ContainerCheckbox/ContainerCheckbox'
import GenericCheckbox from '../GenericCheckbox/GenericCheckbox'

interface CheckboxAttributesProps {
  label: string,
  attributes: AttributeItem[]
}

const CheckboxAttributes = ({ label, attributes }: CheckboxAttributesProps) => {
  const { selectedLocations } = useSelector((state) => state.shedManagement)
  const dispatch = useDispatch()

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target as HTMLInputElement
    const idAttribute = Number(value)

    const allHasAttIdSelected = selectedLocations.every((location) =>
      location.locationAttribute.some((attr) => attr.idAttribute === idAttribute))

    if (allHasAttIdSelected) {
      const updatedSelectedLocations = selectedLocations.map((location) => ({
        ...location,
        locationAttribute: location.locationAttribute.filter((l) => l.idAttribute !== idAttribute),
      }))

      dispatch(actions.setSelectedLocations({ selectedLocations: updatedSelectedLocations }))
      return
    }

    const isAttIdSelected = selectedLocations.some((location) =>
      location.locationAttribute.some((l) => l.idAttribute === idAttribute))

    const updatedSelectedLocations = selectedLocations.map((location) => {
      if (isAttIdSelected) {
        return {
          ...location,
          locationAttribute: location.locationAttribute.filter((l) => l.idAttribute !== idAttribute),
        }
      }
      return location
    })

    const updatedSelectedLocationsWithId = updatedSelectedLocations.map((location) => ({
      ...location,
      locationAttribute: [...location.locationAttribute, { idAttribute }],
    }))

    dispatch(actions.setSelectedLocations({ selectedLocations: updatedSelectedLocationsWithId }))
  }, [dispatch, selectedLocations])

  const attributeValues = useMemo(() => attributes.map((attribute) => {
    let isChecked = false
    let isIndeterminate = false

    if (selectedLocations.length > 0) {
      isChecked = selectedLocations.every((location) =>
        location.locationAttribute.some((l) => l.idAttribute === attribute.id))

      isIndeterminate = !isChecked && selectedLocations.some((location) =>
        location.locationAttribute.some((l) => l.idAttribute === attribute.id))
    }

    return (
      <GenericCheckbox
        key={attribute.id}
        label={attribute.name}
        value={attribute.id}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        onChange={handleChange}
        disabled={selectedLocations.length < 1}
      />
    )
  }), [attributes, handleChange, selectedLocations])

  return (
    <ContainerCheckbox label={label}>
      { attributeValues }
    </ContainerCheckbox>
  )
}

export default CheckboxAttributes
